import { HI_TOP_LEVEL_SLUGS } from "./locale-twins.generated";
import { D1_SERVED_HI_SLUGS } from "./d1-festival-routes.generated";

export const locales = ["en", "hi"] as const;
export type Locale = (typeof locales)[number];

export const defaultLocale: Locale = "en";

/** URL prefix for each locale. Default locale has no prefix (root). */
export const localePrefix: Record<Locale, string> = {
  en: "",
  hi: "/hi",
};

/** BCP 47 language tag for the HTML lang attribute. */
export const localeLang: Record<Locale, string> = {
  en: "en",
  hi: "hi",
};

/** Display name shown in the language toggle UI. */
export const localeLabel: Record<Locale, string> = {
  en: "EN",
  hi: "हिंदी",
};

/**
 * Slugs whose translated page intentionally uses a different URL slug.
 * Keeps language toggle and hreflang alternates from linking to 404s.
 */
export const localeSlugAliases: Partial<Record<Locale, Record<string, string>>> = {
  en: {
    "aaj-ka-rashifal": "rashifal",
    // /lal-kitab-remedies/ redirects to the consolidated English /lal-kitab/ hub.
    "lal-kitab-remedies": "lal-kitab",
    // /remedies/ 301-redirects to /upay/; hreflang must point to the final URL
    remedies: "upay",
    // /talk-to-astrologer/ and /chat-with-astrologer/ 301 to /ai-astrologer/
    // since the 2026-09-30 cleanup release, and their Hindi twins
    // (/hi/jyotishi-se-baat-karen/, /hi/jyotishi-se-chat/) 301 to
    // /hi/muft-jyotish-chat/ since 2026-10-08. A retired slug resolves to the
    // page that is served, in both directions, never to a redirect.
    "talk-to-astrologer": "ai-astrologer",
    "muft-jyotish-chat": "ai-astrologer",
    "chat-with-astrologer": "ai-astrologer",
    "shaadi-ke-liye-jyotish": "astrologer-for-marriage-problem",
    "career-ke-liye-jyotish": "career-astrology-consultation",
    // The Hindi Nadi checker uses the common Hindi spelling of the dosha.
    "nadi-dosh": "nadi-dosha",
  },
  hi: {
    rashifal: "aaj-ka-rashifal",
    "lal-kitab": "lal-kitab-remedies",
    remedies: "upay",
    // Chat-first landing pages: en slug → distinct Hindi-led /hi/<slug>/ URL.
    // The two retired chat variants resolve to the Hindi page of /ai-astrologer/.
    "talk-to-astrologer": "muft-jyotish-chat",
    "chat-with-astrologer": "muft-jyotish-chat",
    "astrologer-for-marriage-problem": "shaadi-ke-liye-jyotish",
    "career-astrology-consultation": "career-ke-liye-jyotish",
    "ai-astrologer": "muft-jyotish-chat",
    "nadi-dosha": "nadi-dosh",
  },
};

/** Detect the URL locale without importing the server-only metadata barrel. */
export function detectLocale(pathname: string): Locale {
  if (pathname.startsWith("/hi/") || pathname === "/hi") return "hi";
  return "en";
}

/** Resolve a localized route without importing metadata/indexability policy. */
export function getLocalePath(slug: string, locale: Locale): string {
  const localizedSlug = localeSlugAliases[locale]?.[slug] ?? slug;
  const base = locale === "hi" ? `/hi/${localizedSlug}` : `/${localizedSlug}`;
  return base.endsWith("/") ? base : `${base}/`;
}

/**
 * Derive "which en slugs have a Hindi twin" from the FILESYSTEM scan
 * (HI_TOP_LEVEL_SLUGS, generated) + the governed D1 festival projection — the
 * hand-maintained list this replaces had drifted BOTH ways (13 entries whose
 * /hi/ app page never existed, 16 real /hi/ pages never offered by the
 * toggle/hreflang). An /hi/<dir>/ page maps back to its en slug via the
 * en-direction alias table (naming decisions stay hand-authored above).
 */
function buildHindiSlugs(): Set<string> {
  const slugs = new Set<string>();
  for (const hiDir of HI_TOP_LEVEL_SLUGS) {
    slugs.add(localeSlugAliases.en?.[hiDir] ?? hiDir);
  }
  for (const slug of D1_SERVED_HI_SLUGS) slugs.add(slug);
  return slugs;
}

export const HINDI_SLUGS = buildHindiSlugs();
